import React from 'react'
import PropTypes from 'prop-types'
import {Tooltip} from '@chakra-ui/react'
import {HideOnDesktop, HideOnMobile} from '@salesforce/retail-react-app/app/components/responsive'
import {
    Box,
    Flex,
    Heading,
    IconButton,
    Text
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {InfoCircle} from '../../icons'
import DisplayPrice from '../../display-price'
import ProductLikes from '../product-likes'
import ProductCallout from './product-callout'
import ProductTileDetailsColorSwatches from './product-tile-details-color-swatches'
import SingleColorSwatch from './single-color-swatch'

const ProductTileDetails = (props) => {
    const {
        styles,
        localizedProductName,
        isShopTheLook,
        showSwatches,
        product,
        isWishlistEnabled,
        showProductLikes,
        isProductInWishlist,
        wishlistCount,
        isHovered,
        handleFavouriteClick,
        isLoadingFreshLikes,
        isWishlistPage,
        productWithFilteredVariants,
        priceData,
        currency,
        productPromotionObject,
        isOOS,
        intl,
        tooltipState,
        colorValue,
        setColorValue,
        setProductId,
        productId,
        isGiftCard,
        responsiveDisplay,
        isProductSet
    } = props
    const {isOpen, onOpen, onClose, onToggle} = tooltipState
    const showPromoSwatches = !isHovered
    const hasPromoName = Boolean(productPromotionObject?.name)

    return (
        <Box {...styles.detailsWrapper} {...(responsiveDisplay?.detailsWrapperOverride || {})}>
            {/* Title */}
            <Flex {...styles.bodyText} {...styles.titleRow}>
                {isShopTheLook ? (
                    <Text
                        as="h3"
                        variant="plpH3"
                        {...styles.title}
                        data-testid="product-name"
                        sx={responsiveDisplay?.productName}
                    >
                        {localizedProductName}
                    </Text>
                ) : (
                    <Text
                        as="h2"
                        {...styles.title}
                        data-testid="product-name"
                        sx={responsiveDisplay?.productName}
                    >
                        {localizedProductName}
                    </Text>
                )}

                {/* Single color swatch on Mobile */}
                {!isGiftCard && (
                    <>
                        <HideOnDesktop>
                            {showSwatches && (
                                <Box sx={responsiveDisplay?.colorSwatches}>
                                    <SingleColorSwatch
                                        swatchData={product?.c_tilesSwatches}
                                        styles={styles}
                                    />
                                </Box>
                            )}
                        </HideOnDesktop>

                        {/* Product Likes */}
                        {isWishlistEnabled && showProductLikes && (
                            <HideOnMobile>
                                <Box sx={responsiveDisplay?.wishlistIcon}>
                                    <Flex {...styles.productLikesWrapper}>
                                        <ProductLikes
                                            isFavourite={isProductInWishlist}
                                            wishlistCount={wishlistCount}
                                            isHovered={isHovered}
                                            onFavouriteToggle={handleFavouriteClick}
                                            productName={localizedProductName}
                                            isLoadingFreshLikes={isLoadingFreshLikes}
                                        />
                                    </Flex>
                                </Box>
                            </HideOnMobile>
                        )}
                    </>
                )}
            </Flex>

            {!isGiftCard && isWishlistPage && productWithFilteredVariants?.variationValues && (
                <Text {...styles.wishlistVariantText}>
                    {productWithFilteredVariants.variationValues.size && (
                        <>
                            {intl.formatMessage({
                                id: 'product_tile.label.size',
                                defaultMessage: 'Size'
                            })}
                            : {productWithFilteredVariants.variationValues.size}{' '}
                        </>
                    )}
                    {productWithFilteredVariants.variationValues.length && (
                        <>
                            {intl.formatMessage({
                                id: 'product_tile.label.length',
                                defaultMessage: 'Length'
                            })}
                            : {productWithFilteredVariants.variationValues.length}
                        </>
                    )}
                </Text>
            )}

            {!isGiftCard && !isProductSet && (
                <Box sx={responsiveDisplay?.productPrice}>
                    {/* Price updates reactively from priceData — no skeleton. */}
                    <>
                            {/* Price */}
                            <Flex {...styles.bodyText} {...styles.priceRow}>
                                <DisplayPrice
                                    priceData={priceData}
                                    currency={currency}
                                    currentPriceProps={{
                                        fontWeight: 'medium'
                                    }}
                                    listPriceProps={{
                                        fontWeight: 'medium'
                                    }}
                                />
                                {productPromotionObject?.calloutMsg && (
                                    <Tooltip
                                        isOpen={isOpen}
                                        aria-label={productPromotionObject?.calloutMsg}
                                        hasArrow
                                        arrowShadowColor="gray.50"
                                        label={productPromotionObject?.calloutMsg}
                                        {...styles.toolTip}
                                        placement="right"
                                    >
                                        <IconButton
                                            onMouseEnter={onOpen}
                                            onMouseLeave={onClose}
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                e.preventDefault()
                                                onToggle()
                                            }}
                                            variant="unstyled"
                                            {...styles.toolTipButton}
                                            icon={<InfoCircle {...styles.toolTipIcon} />}
                                            aria-label="Tooltip"
                                        />
                                    </Tooltip>
                                )}
                                {isWishlistPage && isOOS && (
                                    <Text {...styles.oosText}>
                                        {intl.formatMessage({
                                            id: 'product_tile.label.oos',
                                            defaultMessage: 'Sold Out'
                                        })}
                                    </Text>
                                )}
                            </Flex>
                            {hasPromoName && (
                                <Text
                                    {...styles.bodyText}
                                    {...styles.promotionText}
                                    color={
                                        priceData.isOnSale
                                            ? 'webTypo.textPrimary'
                                            : 'webTypo.textSale'
                                    }
                                    className="product-promotion"
                                >
                                    {productPromotionObject.name}
                                </Text>
                            )}
                    </>
                </Box>
            )}

            {!isGiftCard && (
                <Box display="flex" flexDirection="column">
                    {/* Product promotions */}
                    <Box
                        data-testid="product-callout-slot"
                        order={showPromoSwatches ? 1 : 2}
                        sx={responsiveDisplay?.productTags}
                    >
                        <ProductCallout
                            product={product}
                            styles={styles}
                            intl={intl}
                            props={{showPromoSwatches}}
                        />
                    </Box>

                    {/* Product tile color swatches */}
                    <HideOnMobile>
                        <Box
                            data-testid="product-swatches-slot"
                            order={1}
                            sx={responsiveDisplay?.colorSwatches}
                        >
                            {isHovered && !isProductSet && (
                                <ProductTileDetailsColorSwatches
                                    product={product}
                                    styles={styles}
                                    colorValue={colorValue}
                                    setColorValue={setColorValue}
                                    setProductId={setProductId}
                                    productId={productId}
                                />
                            )}
                        </Box>
                    </HideOnMobile>
                </Box>
            )}
        </Box>
    )
}

ProductTileDetails.propTypes = {
    styles: PropTypes.object,
    localizedProductName: PropTypes.string,
    isShopTheLook: PropTypes.bool,
    showSwatches: PropTypes.bool,
    product: PropTypes.object,
    isWishlistEnabled: PropTypes.bool,
    showProductLikes: PropTypes.bool,
    isProductInWishlist: PropTypes.bool,
    wishlistCount: PropTypes.number,
    isHovered: PropTypes.bool,
    handleFavouriteClick: PropTypes.func,
    isLoadingFreshLikes: PropTypes.bool,
    isWishlistPage: PropTypes.bool,
    productWithFilteredVariants: PropTypes.object,
    priceData: PropTypes.object,
    currency: PropTypes.string,
    productPromotionObject: PropTypes.object,
    isOOS: PropTypes.bool,
    intl: PropTypes.object,
    colorValue: PropTypes.string,
    setColorValue: PropTypes.func,
    setProductId: PropTypes.func,
    productId: PropTypes.string,
    isGiftCard: PropTypes.bool,
    tooltipState: PropTypes.shape({
        isOpen: PropTypes.bool,
        onOpen: PropTypes.func,
        onClose: PropTypes.func,
        onToggle: PropTypes.func
    }),
    responsiveDisplay: PropTypes.object
}

export default ProductTileDetails
